/* Product rows (Round 4; markup in sections/sp-collection-strips.liquid and the product page's rows, styles in
   sp-strips.css). The row itself is native scrolling without snapping, so touch, trackpads and keyboard need nothing
   from here. This only adds the arrows (Round 19): shown from 750px wide (phones scroll by touch) when the row
   overflows; at an end the arrow that way is marked aria-disabled (dimmed, does nothing, keeps keyboard focus). A click
   moves the row by the cards wholly in view, landing with a card's edge on the left margin (smoothly, or at once with
   reduced motion). Scroll and resize work is batched to one animation frame. */
const wide = matchMedia('(min-width: 750px)');
const reduced = matchMedia('(prefers-reduced-motion: reduce)');

function initStrip(strip) {
  if (strip.dataset.spStripReady) return;
  strip.dataset.spStripReady = 'true';
  const track = strip.querySelector('[data-sp-strip-track]');
  const nav = strip.querySelector('[data-sp-strip-nav]');
  const prev = strip.querySelector('[data-sp-strip-prev]');
  const next = strip.querySelector('[data-sp-strip-next]');
  if (!track || !nav || !prev || !next) return;
  let frame = 0;
  const update = () => {
    frame = 0;
    const max = track.scrollWidth - track.clientWidth;
    nav.hidden = !wide.matches || max <= 2;
    prev.setAttribute('aria-disabled', String(track.scrollLeft <= 2));
    next.setAttribute('aria-disabled', String(track.scrollLeft >= max - 2));
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(update);
  };
  const step = (direction, button) => {
    if (button.getAttribute('aria-disabled') === 'true') return;
    const [first, second] = track.children;
    if (!first) return;
    // A card and its gap; the cards wholly in view from the left margin; the next view starts on a card's edge.
    const pitch = second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
    const inView = Math.max(1, Math.floor((track.clientWidth - parseFloat(getComputedStyle(track).paddingLeft)) / pitch));
    const max = track.scrollWidth - track.clientWidth;
    const left = Math.min(max, Math.max(0, (Math.round(track.scrollLeft / pitch) + direction * inView) * pitch));
    track.scrollTo({ left, behavior: reduced.matches ? 'auto' : 'smooth' });
  };
  prev.addEventListener('click', () => step(-1, prev));
  next.addEventListener('click', () => step(1, next));
  track.addEventListener('scroll', schedule, { passive: true });
  new ResizeObserver(schedule).observe(track);
  wide.addEventListener('change', schedule);
  update();
}

document.querySelectorAll('[data-sp-strip]').forEach(initStrip);
document.addEventListener('shopify:section:load', (event) =>
  event.target.querySelectorAll('[data-sp-strip]').forEach(initStrip),
);

/* Product page strips (Round 5; sections/sp-product-strips.liquid). "Similar styles" (Round 10A) arrives with the page. Here:
   "Recently viewed", the styles this browser opened before. The script knows at once whether there is any history; if
   so it shows the strip straight away with its height reserved (the row above's height, else an estimate from the
   card width), then asks sections/sp-strip-cards.liquid for the cards and puts them in that space, so nothing below
   moves however fast the page is scrolled. If none of those styles exists any more, the strip goes again (it is below
   the first screen then). The list lives in localStorage ("sp-viewed", newest first, 12 at most); storage can be
   missing or blocked (private windows), so every read and write is guarded and the page works without it. */
const VIEWED_KEY = 'sp-viewed';
const readViewed = () => {
  try {
    const list = JSON.parse(localStorage.getItem(VIEWED_KEY) || '[]');
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && /^[a-z0-9-]+$/.test(h)) : [];
  } catch {
    return [];
  }
};
const remember = (handle) => {
  try {
    localStorage.setItem(
      VIEWED_KEY,
      JSON.stringify([handle, ...readViewed().filter((h) => h !== handle)].slice(0, 12)),
    );
  } catch {}
};

async function fetchCards(url) {
  try {
    const response = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!response.ok) return [];
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return [...doc.querySelectorAll('[data-sp-strip-cards] > .sp-strip-item')];
  } catch {
    return [];
  }
}

function initProductStrips(root) {
  if (root.dataset.spStripsReady) return;
  root.dataset.spStripsReady = 'true';
  root.querySelectorAll('[data-sp-strip]:not([hidden])').forEach(initStrip);
  const { productHandle, limit, searchUrl } = root.dataset;
  const viewed = readViewed()
    .filter((h) => h !== productHandle)
    .slice(0, Number(limit) || 8);
  if (productHandle) remember(productHandle);
  const recent = root.querySelector('[data-sp-strip-source="recent"]');
  const track = recent?.querySelector('[data-sp-strip-track]');
  if (!recent || !track || !searchUrl || !viewed.length) return;
  // Reserve the row's height before showing it: the Similar styles track if there is one, else a card's photo (one
  // column of the strip, square) plus its text.
  const other = root.querySelector('[data-sp-strip-source="similar"] [data-sp-strip-track]');
  recent.hidden = false;
  const show = Number(getComputedStyle(track).getPropertyValue('--sp-strip-show')) || 2.4;
  const reserve = other?.offsetHeight || Math.round(track.clientWidth / show + 150);
  track.style.minHeight = `${reserve}px`;
  const url = `${searchUrl}?q=${encodeURIComponent(viewed.join(' '))}&options%5Bprefix%5D=none&section_id=sp-strip-cards`;
  fetchCards(url).then((items) => {
    if (!items.length) {
      recent.hidden = true;
      return;
    }
    track.replaceChildren(...items.map((item) => document.importNode(item, true)));
    initStrip(recent);
  });
}

document.querySelectorAll('[data-sp-product-strips]').forEach(initProductStrips);
document.addEventListener('shopify:section:load', (event) =>
  event.target.querySelectorAll('[data-sp-product-strips]').forEach(initProductStrips),
);
